<script lang="ts">
    import * as Select from "$lib/components/ui/select";
    import Label from "@/lib/components/ui/label/label.svelte";
    import Input from "@/lib/components/ui/input/input.svelte";
    import Switch from "@/lib/components/ui/switch/switch.svelte";
    import Checkbox from "@/lib/components/ui/checkbox/checkbox.svelte";
    import { createEventDispatcher } from "svelte";
    import { durationSelections, type Availability, type DayIntensity } from "@/Common/constants";
    import Button from "@/lib/components/ui/button/button.svelte";
    import Check from "svelte-radix/Check.svelte";
    import { isSystemChosen, usualIntensity as usualIntensityOf } from "./availability";
    import { cn } from "@/lib/utils";
    import HelpTooltip from "@/Common/HelpTooltip.svelte";

    export let defaultAvailability: Availability
    export let id: string
    export let dowLabel: string
    // The day's usual duration (minutes) and start time ("HH:MM"), what the system choosing comes to, and whether its riding history
    // makes it a high intensity day. Without it the values shown for the system's choice are a plain hour at 9:00, and no day is high.
    export let medians: { duration: number, time: string, high?: boolean } | null = null

    // For the planner, where the popover is for one day of the calendar: a toggle between that day alone and every such weekday
    // (scopeLabel names it, "Wednesdays"), and a Reset that sends the day back to the weekday's setting. resetDot marks the day:
    // red when it differs from the weekday's setting, yellow when the system doesn't choose.
    export let scopeLabel: string | null = null
    export let scope: "day" | "weekday" = "day"
    export let resetDot: "red" | "yellow" | null = null

    // Whether there is a Save button, for a caller that keeps its own copy until then and takes 'save'. Without it the edits are the
    // caller's as they are made (the profile and program settings, saved with the page), and it is settled when it closes.
    export let showSave = false
    // Whether it names itself: its own title line, which a dialog gives with its own title instead
    export let showTitle = true
    // For the title to name the day it is for ("Wed, Nov 4"), and the weekdays ("Wednesdays") while those are being set;
    // without it the title is the plain one. titleClass restyles it.
    export let dateLabel = ""
    export let titleClass = "font-medium leading-none border-b pb-1 text-muted-foreground"
    // Greyed out while there is nothing to save
    export let saveDisabled = false

    // 'save' is sent with the day's availability when Save is pressed;
    // 'reset' and 'scope' when those are used.
    const dispatch = createEventDispatcher<{ save: Availability, reset: void, scope: "day" | "weekday" }>()

    // Each edit is written to the day's availability as it is made, and what the popover shows comes from it, so it has no
    // copy to keep in step: opening it changes nothing, and a change made elsewhere (a suggestion of FrontierAdjustDialog,
    // say) shows here at once. "Let the system choose" is ticked when it chooses everything (duration, time, adjusting
    // progressively and intensity), and those are greyed out. A day with any of them set shows unticked, with the usual
    // for the duration or time the system still chooses.
    $: day = defaultAvailability
    $: durationAdvisor = day.duration === -1
    $: timeAdvisor = day.time === "advisor"
    $: useAdvisor = isSystemChosen(day, usualIntensity)
    // Shown, not saved, wherever the system chooses the duration or the time: the day's usual, the duration to the
    // nearest choice of the list. They are also the values that unticking the box sets.
    $: usualDuration = medians
        ? durationSelections.reduce((nearest, s) => Math.abs(s.value - medians.duration) < Math.abs(nearest.value - medians.duration) ? s : nearest).value
        : 60
    $: usualTime = medians?.time ?? "09:00"
    // As the setup wizard sets a new user's days: High where the history makes it so. While the system chooses, the intensity is
    // that, stored and shown, so a day it chooses everything for is saved as High on those days.
    $: usualIntensity = usualIntensityOf(medians)
    $: shownIntensity = useAdvisor ? usualIntensity : (day.intensity ?? "advisor")
    // The list is in 15 minute steps. A saved duration in between (1h 20m, say, from the old planner's custom duration) is added to
    // it, so it shows and stays as it is.
    const durationLabel = (minutes: number) => {
        const h = Math.floor(minutes / 60), m = Math.round(minutes % 60)
        return [h ? `${h} Hour${h > 1 ? "s" : ""}` : "", m ? `${m} Mins` : ""].filter(Boolean).join(" ")
    }
    $: durationOptions = day.duration > 0 && !durationSelections.some(s => s.value === day.duration)
        ? [...durationSelections, { label: durationLabel(day.duration), value: day.duration }].sort((a, b) => a.value - b.value)
        : durationSelections
    $: selectedDuration = { value: durationAdvisor ? usualDuration : day.duration,
        label: durationOptions.find(f => f.value === (durationAdvisor ? usualDuration : day.duration))?.label ?? "Select a duration..." }
    $: timeValue = timeAdvisor ? usualTime : day.time.slice(0, 5) // Saved times have seconds, the input does not

    function write(change: Partial<Availability>) {
        const next = { ...defaultAvailability, ...change }
        // Without a Save button the edits are the page's at once (Save All may send them while this is still open), so they are
        // what is shown: a field the system chooses keeps its usual, and a system-chosen day the usual intensity
        defaultAvailability = showSave ? next : settled(next)
    }
    function settled(a: Availability): Availability {
        if (!a.availability) return a
        if (isSystemChosen(a, usualIntensity)) return a.intensity === usualIntensity ? a : { ...a, intensity: usualIntensity }
        const durationSystem = a.duration === -1, timeSystem = a.time === "advisor"
        return durationSystem || timeSystem ? { ...a, duration: durationSystem ? usualDuration : a.duration, time: timeSystem ? usualTime : a.time } : a
    }
    // What the day had before the system was given the choice, put back when the box is unticked again with nothing set
    // in between; otherwise unticking sets the values shown. Forgotten when the scope changes or Reset is used: those replace
    // the settings being edited.
    let beforeAdvisor: Pick<Availability, "duration" | "time" | "fixed_availability" | "intensity"> | null = null
    function toggleAdvisor() {
        if (!useAdvisor) {
            beforeAdvisor = { duration: day.duration, time: day.time, fixed_availability: day.fixed_availability, intensity: day.intensity }
            write({ duration: -1, time: "advisor", fixed_availability: false, intensity: usualIntensity })
        } else {
            write(beforeAdvisor && !isSystemChosen({ ...day, ...beforeAdvisor }, usualIntensity)
                ? beforeAdvisor : { duration: usualDuration, time: usualTime, intensity: (day.intensity ?? "advisor") === "advisor" ? usualIntensity : day.intensity })
            beforeAdvisor = null
        }
    }
    // What the popover shows for a day with some of it chosen by the system is what is kept: a day left at Either while the system
    // chooses is stored as the usual intensity, and one with only the duration or the time chosen by the system keeps the usual of
    // that one.
    function settle() {
        if (day.availability && useAdvisor && day.intensity !== usualIntensity) {
            write({ intensity: usualIntensity })
        } else if (day.availability && !useAdvisor && (durationAdvisor || timeAdvisor)) {
            write({ duration: durationAdvisor ? usualDuration : day.duration, time: timeAdvisor ? usualTime : day.time })
        }
    }
    function save() {
        settle()
        dispatch("save", defaultAvailability)
    }

    // The usual values as the explanations name them
    const WEEKS = 12 // Weeks of past activities behind the usual (AVERAGE_WEEKS)
    const nearestLabel = (minutes: number) => durationSelections.reduce((nearest, s) => Math.abs(s.value - minutes) < Math.abs(nearest.value - minutes) ? s : nearest).label
    const clock = (time: string) => { const [h, m] = time.split(":").map(Number); return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}` }

    // Shortened under the Intensity heading
    const intensityChoices: { label: string, value: DayIntensity }[] = [
        { label: 'Low', value: 'low' },
        { label: 'Either', value: 'advisor' },
        { label: 'High', value: 'high' },
    ]

    
</script>


    <div class="grid gap-4">
        {#if showTitle}
            <div class={titleClass}>
                {#if dateLabel}
                    Set your availability for {scope === "weekday" && scopeLabel ? scopeLabel : dateLabel}
                {:else}
                    Set your availability<span class="sr-only"> for {dowLabel}</span>
                {/if}
            </div>
        {/if}

        {#if scopeLabel}
            <!-- Native radios, as the intensity choices below -->
            <fieldset class="grid grid-cols-2 gap-1 rounded bg-slate-100 p-1 -mt-1">
                <legend class="sr-only">Applies to</legend>
                {#each [["day", "This Day"], ["weekday", scopeLabel]] as [value, label]}
                    <label class="relative">
                        <input type="radio" class="peer sr-only" name="{id}-scope" {value} checked={scope === value}
                            on:change={() => { scope = value === "day" ? "day" : "weekday"; beforeAdvisor = null; dispatch("scope", scope) }}/>
                        <span class={cn("block rounded px-2 py-1 text-center text-sm leading-tight cursor-pointer transition-colors text-slate-600",
                            "peer-checked:bg-xerteal-600 peer-checked:text-white peer-checked:font-semibold",
                            "peer-focus-visible:ring-2 peer-focus-visible:ring-xerteal-400")}>
                            {label}
                        </span>
                    </label>
                {/each}
            </fieldset>
        {/if}
        
        <div class="grid gap-3.5">
            
            <div class="space-y-2.5">

                <div class="flex gap-4 items-center border-b pb-3.5 -mt-0.5">
                    <Label class="mb-0" for="{id}-available">Available</Label>
                    <Switch id="{id}-available" checked={!!day.availability} onCheckedChange={(checked) => write({ availability: !!checked })}/>
                </div>

                <!-- A plain button: the popover can be inside a form (FrontierAdjustDialog), which it would otherwise submit -->
                <div class="space-y-1 pb-2.5 border-b">
                    <div class="flex flex-wrap items-center gap-x-5 gap-y-1">
                        <button type="button" role="checkbox" aria-checked={useAdvisor} disabled={!day.availability} class="inline-flex items-center gap-2 text-left disabled:cursor-not-allowed disabled:opacity-50" on:click={toggleAdvisor}>
                            <Label class="mb-0 leading-tight cursor-pointer">Let the system choose?</Label>
                            <!-- Drawn here rather than a Checkbox, which keeps its old look when its value changes while the popover is open -->
                            <span id="{id}-advisor" aria-hidden="true" data-state={useAdvisor ? "checked" : "unchecked"}
                                class={cn("box-content h-4 w-4 shrink-0 rounded-sm border border-primary shadow flex items-center justify-center", useAdvisor && "bg-primary text-primary-foreground")}>
                                {#if useAdvisor}<Check class="h-3.5 w-3.5" />{/if}
                            </span>
                        </button>
                    </div>
                    <p class="m-0 text-xs leading-snug text-muted-foreground">The system considers your last {WEEKS} weeks averages in choosing your training.</p>
                </div>

            </div>

            <div class="grid gap-2 border-b pb-4">
                <div class="grid grid-cols-3 items-center gap-4" class:opacity-50={useAdvisor}>
                    <div class="flex items-center gap-0.5">
                        <Label class="mb-0" for={`${id}-duration`}>Duration</Label>
                        <HelpTooltip label="About Duration" iconClass="-my-1 h-6 w-6 text-gray-500">
                            <p class="m-0 text-sm leading-snug">The longest a day's training is planned to run: workouts and rides are fitted to it.
                        <strong class="font-semibold">The system chooses</strong> your usual ride length on {dowLabel}s, the median of your rides{medians ? `, about ${nearestLabel(medians.duration)}` : ", or 1 hour while there is no history yet"}.</p>
                        </HelpTooltip>
                    </div>
                    <div class="col-span-2 min-w-0">
                        <Select.Root disabled={!day.availability || useAdvisor} selected={selectedDuration} items={durationOptions}
                            onSelectedChange={(v) => { if (v) write({ duration: v.value }) }}
                        >
                            <Select.Trigger id="{id}-duration">
                                <Select.Value placeholder="Theme"/>
                            </Select.Trigger>
                            <Select.Content class="overflow-auto max-h-48">
                                {#each durationOptions as duration}
                                    <Select.Item value={duration.value}>{duration.label}</Select.Item>
                                {/each}
                            </Select.Content>
                            <Select.Input/>
                        </Select.Root>
                    </div>
                </div>

                <div class="grid grid-cols-3 items-center gap-4" class:opacity-50={useAdvisor}>
                    <div class="flex items-center gap-0.5">
                        <Label class="mb-0" for={`${id}-time`}>Time</Label>
                        <HelpTooltip label="About Time" iconClass="-my-1 h-6 w-6 text-gray-500">
                            <p class="m-0 text-sm leading-snug">When training starts: workouts are planned from this time.
                        <strong class="font-semibold">The system chooses</strong> your usual start time on {dowLabel}s, the median start time of your rides{medians ? ` (${clock(medians.time)})` : ", or 9:00 AM while there is no history yet"}.</p>
                        </HelpTooltip>
                    </div>
                    <div class="col-span-2 min-w-0">
                        <Input class="peer box-border min-w-0 w-full" id="{id}-time" disabled={!day.availability || useAdvisor} type="time" value={timeValue}
                            on:change={(e) => { const time = e.currentTarget.value; if (time) write({ time }) }}
                        />
                    </div>
                </div>
    

                <!-- On one line with its box beside it, as "Let the system choose?" is -->
                <div class="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1" class:opacity-50={useAdvisor}>
                    <div class="flex items-center gap-0.5">
                        <Label class="mb-0 whitespace-nowrap leading-tight" for="{id}-flex">{"Adjust durations progressively?"}</Label>
                        <HelpTooltip label="About Adjust durations progressively" iconClass="-my-1 h-6 w-6 text-gray-500">
                            <p class="m-0 text-sm leading-snug">Ticked, the time you have grows with your training load as your fitness builds, so a ride can run longer than the duration when you are ready for it. Unticked, a ride never goes over the duration.
                        <strong class="font-semibold">The system chooses</strong> to adjust progressively.</p>
                        </HelpTooltip>
                    </div>
                    <Checkbox disabled={!day.availability || useAdvisor} id="{id}-flex" checked={!day.fixed_availability}
                        onCheckedChange={(checked) => write({ fixed_availability: !checked })}
                    />
                </div>
            </div>

            <!-- Either lets Forecast AI choose; high-intensity days get intervals (or rest), low-intensity days none -->
            <!-- Native radios: arrow keys move between the choices, and the group is a single Tab stop -->
            <fieldset class="grid gap-2 border-b pb-4" disabled={!day.availability || useAdvisor}>
                <legend class="font-medium text-sm leading-none mb-2 flex items-center gap-0.5">Intensity
                        <HelpTooltip label="About Intensity" iconClass="-my-1 h-6 w-6 text-gray-500">
                            <p class="m-0 text-sm leading-snug">High days get interval training, Low days easy riding with no intervals, and Either leaves it to Forecast AI.
                        <strong class="font-semibold">The system chooses</strong> High on the weekdays where at least 8% of your XSS was high or peak intensity, and Either on the others.
                        {medians ? (medians.high ? `${dowLabel}s are High for you.` : `${dowLabel}s are not, so they stay Either.`) : "With no history yet it chooses Either."}</p>
                        </HelpTooltip>
                </legend>
                <div class={cn("grid grid-cols-3 gap-1", useAdvisor && "opacity-50")}>
                    {#each intensityChoices as choice}
                        <label class="relative">
                            <input type="radio" class="peer sr-only" name="{id}-intensity" value={choice.value}
                                checked={shownIntensity === choice.value}
                                on:change={() => write({ intensity: choice.value })}/>
                            <span class={cn("block rounded border px-1 py-1.5 text-center text-xs leading-tight transition-colors cursor-pointer",
                                "border-slate-300 text-slate-600 hover:border-xerteal-400",
                                "peer-checked:border-xerteal-600 peer-checked:bg-xerteal-600 peer-checked:text-white peer-checked:font-semibold",
                                "peer-focus-visible:ring-2 peer-focus-visible:ring-xerteal-400 peer-disabled:opacity-50 peer-disabled:cursor-not-allowed")}>
                                {choice.label}
                            </span>
                        </label>
                    {/each}
                </div>
            </fieldset>

        </div>
        {#if scopeLabel || showSave}
        <div class={cn("flex items-center", scopeLabel ? "justify-between" : "justify-end")}>
            {#if scopeLabel}
                <Button type="button" variant="outline" size="sm" on:click={() => { beforeAdvisor = null; dispatch("reset") }}
                    title={scope === "day" ? "Returns this day to its weekday's setting" : `Returns the setting for all ${scopeLabel} to how it was`}>
                    <span class="inline-flex items-center gap-2">Reset
                        {#if resetDot}<span class={cn("inline-block h-2 w-2 rounded-full", resetDot === "red" ? "bg-red-500" : "bg-yellow-500")}
                            title={resetDot === "red" ? "Different from its weekday's setting" : "The system doesn't choose everything"}></span>{/if}
                    </span>
                </Button>
            {/if}
            {#if showSave}
                <!-- The container closes with it: a popover's or a dialog's Close, passed in -->
                <slot name="save" {save} disabled={saveDisabled}>
                    <Button size="sm" disabled={saveDisabled} on:click={save}>Save</Button>
                </slot>
            {/if}
        </div>
        {/if}

    </div>